import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import Modal from "@/components/base/Modal";
import {
  useSupportTickets,
  updateTicketStatus,
  logAudit,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  type SupportTicket,
} from "@/hooks/useAdmin";

type StatusFilter = "tumu" | "yeni" | "inceleniyor" | "cozuldu" | "kapatildi";

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "tumu", label: "Tümü" },
  { key: "yeni", label: "Yeni" },
  { key: "inceleniyor", label: "İnceleniyor" },
  { key: "cozuldu", label: "Çözüldü" },
  { key: "kapatildi", label: "Kapatıldı" },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminDestek() {
  const { data, loading, error, reload } = useSupportTickets();
  const [filter, setFilter] = useState<StatusFilter>("tumu");
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const list = data ?? [];
  const filtered = list.filter((t) => filter === "tumu" || t.status === filter);

  const countBy = (status: string) => list.filter((t) => t.status === status).length;

  const handleStatusChange = async (ticket: SupportTicket, status: string) => {
    setUpdatingId(ticket.id);
    const err = await updateTicketStatus(ticket.id, status);
    setUpdatingId(null);
    if (!err) {
      await logAudit("ticket_durum", "destek", `${ticket.subject} → ${status}`);
      reload();
      if (selected?.id === ticket.id) {
        setSelected({ ...selected, status });
      }
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Destek Talepleri"
        description="Kullanıcılardan gelen destek taleplerini görüntüle ve yönet."
      />

      {/* Filtreler + özet */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`h-9 px-3 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100"
            }`}
          >
            {f.label}
            {f.key !== "tumu" && ` (${countBy(f.key)})`}
          </button>
        ))}
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-question-answer-line"
          title="Henüz destek talebi yok"
          description="Kullanıcılar iletişim formunu doldurdukça talepler burada görünecek."
        />
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60">
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Talep</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Ad</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tarih</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Durum</th>
                  <th className="px-4 py-3 text-right font-semibold text-foreground-700">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                    <td className="px-4 py-3 max-w-[280px]">
                      <p className="font-medium text-foreground-950 truncate">{t.subject}</p>
                      <p className="text-xs text-foreground-500 truncate">{t.email}</p>
                    </td>
                    <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{t.name}</td>
                    <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{formatDate(t.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${TICKET_STATUS_COLORS[t.status] ?? "bg-background-100 text-foreground-600"}`}
                      >
                        {TICKET_STATUS_LABELS[t.status] ?? t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <select
                          value={t.status}
                          onChange={(e) => handleStatusChange(t, e.target.value)}
                          disabled={updatingId === t.id}
                          className="h-8 px-2 rounded-md border border-background-200 bg-background-50 text-xs text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                        >
                          {Object.entries(TICKET_STATUS_LABELS).map(([k, label]) => (
                            <option key={k} value={k}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setSelected(t)}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition-colors"
                          aria-label="Detay"
                          title="Detay"
                        >
                          <i className="ri-eye-line" aria-hidden="true"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detay modalı */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Destek Talebi Detayı"
        titleId="ticket-detail-title"
        id="ticket-detail-modal"
        maxWidthClass="max-w-lg"
      >
        {selected && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-heading font-semibold text-base text-foreground-950">{selected.subject}</p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {selected.name} · {selected.email}
                </p>
                <p className="text-xs text-foreground-400 mt-0.5">{formatDate(selected.createdAt)}</p>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${TICKET_STATUS_COLORS[selected.status] ?? "bg-background-100 text-foreground-600"}`}
              >
                {TICKET_STATUS_LABELS[selected.status] ?? selected.status}
              </span>
            </div>

            <div className="rounded-md bg-background-100/60 p-4">
              <p className="text-sm text-foreground-800 leading-relaxed whitespace-pre-line">
                {selected.message || "Mesaj yok."}
              </p>
            </div>

            <div>
              <span className="block text-sm font-semibold text-foreground-800 mb-1.5">Durumu Değiştir</span>
              <select
                value={selected.status}
                onChange={(e) => handleStatusChange(selected, e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                {Object.entries(TICKET_STATUS_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}